import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import { Button, ConfirmModal, Flex } from 'nallian-shared-ui/lib/components';
import HasRight from 'nallian-shared-ui/lib/layout/rights/HasRight';
import { Table, Params, Merge, Menu, Actions } from 'nallian-table-builder-ui';
import { useBoolState } from 'nallian-shared-ui/lib/hooks';
import apiRoutes from '../../../../app/api/routes';
import { ACTIONS, COMMUNITY_DETAIL_TABS } from '../../../communities/constants';
import { TABS } from '../../../organisations/constants';
import PopupAndCopy from '../../../app/components/PopupAndCopy';
import { mapAppSubscriptions } from '../../../organisations/helpers/appSubscriptions/mapAppSubscriptions';
import CreateFeatureSubscriptionModal from './CreateFeatureSubscriptionModal';
import removeFeatureSubscriptionService from '../../services/removeFeatureSubscriptionService';
import removeOrgAppSubscriptionService from '../../services/removeOrgAppSubscriptionService';
import updateOrgAppSubscriptionService from '../../services/updateOrgAppSubscriptionService';
import orgAppSubscriptionsLookupService from '../../services/orgAppSubscriptionsLookupService';
import TypeToConfirmModal from '../../../app/components/TypeToConfirmModal';
import AddSubscriptionModal from './AddSubscriptionModal';

// The org-app DELETE needs the row's community to resolve the communityApp id;
// hide the menu for rows without a community.
const SubscriptionMenu = ({ item, children }) => (item?.community?.id ? children : null);

SubscriptionMenu.propTypes = {
  item: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};

const FeatureSubscriptions = ({
  appId,
  installationId,
  featureId,
  installationName,
  appName,
  canCreate,
  dispatchRemoveFeatureSubscriptionDelete,
  removeFeatureSubscriptionFetch,
  dispatchRemoveOrgAppSubscriptionDelete,
  removeOrgAppSubscriptionFetch,
  dispatchUpdateOrgAppSubscriptionPut,
  updateOrgAppSubscriptionFetch,
  dispatchOrgAppSubscriptionsLookupGet,
  orgAppSubscriptionsLookupFetch,
  feature,
}) => {
  const { t } = useTranslation();

  const resource = `featureSubscriptions_${appId}_${installationId}_${featureId}`;

  const [modalOpen, setModalOpen, setModalClosed] = useBoolState(false);
  const [addModalOpen, setAddModalOpen, setAddModalClosed] = useBoolState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [removalItem, setRemovalItem] = useState(null);
  const [orgAppRemovalItem, setOrgAppRemovalItem] = useState(null);

  const handleSubscriptionCreated = useCallback(() => {
    setRefreshKey(key => key + 1);
    setAddModalClosed();
  }, [setRefreshKey, setAddModalClosed]);

  const handleCancel = useCallback(() => setRemovalItem(null), [setRemovalItem]);
  const handleSetRemovalItem = useCallback((_, item) => setRemovalItem(item), [setRemovalItem]);

  const handleRemove = useCallback(() => {
    if (removalItem) {
      dispatchRemoveFeatureSubscriptionDelete(
        appId,
        installationId,
        featureId,
        removalItem.subscriptionId,
        () => setRemovalItem(null),
        resource,
        t
      );
    }
  }, [
    removalItem,
    dispatchRemoveFeatureSubscriptionDelete,
    appId,
    installationId,
    featureId,
    resource,
    t,
  ]);

  const handleSetOrgAppRemovalItem = useCallback(
    (_, item) => {
      setOrgAppRemovalItem(item);
      // The remove decision (PUT remaining features vs DELETE the whole
      // organisation-app) needs the org's current feature subscriptions.
      if (item?.organisation?.id) {
        dispatchOrgAppSubscriptionsLookupGet(item.organisation.id, installationId);
      }
    },
    [setOrgAppRemovalItem, dispatchOrgAppSubscriptionsLookupGet, installationId]
  );
  const handleCancelOrgAppRemove = useCallback(() => setOrgAppRemovalItem(null), [
    setOrgAppRemovalItem,
  ]);

  const handleOrgAppRemove = useCallback(() => {
    if (orgAppRemovalItem) {
      const orgApps = orgAppSubscriptionsLookupFetch.value || [];
      const orgApp = orgApps.find(oa => oa.community?.id === orgAppRemovalItem.community?.id);
      const clearItem = () => setOrgAppRemovalItem(null);

      if ((orgApp?.communityAppFeatures || []).length > 1) {
        // The org has other feature subscriptions on this community app:
        // PUT the remaining features instead of revoking the entire app
        // (same pattern as AppSubscriptions.handleRemoveFeature).
        dispatchUpdateOrgAppSubscriptionPut(
          mapAppSubscriptions(orgAppRemovalItem.organisation?.id, orgApp.id, orgApp, undefined, {
            appInstallationFeature: { tag: feature?.tag },
          }),
          clearItem,
          { resource, id: orgAppRemovalItem.subscriptionId },
          'removeOrgSubscriptionSuccess',
          'removeOrgSubscriptionFailed',
          t
        );
      } else {
        dispatchRemoveOrgAppSubscriptionDelete(
          {
            organisation: { id: orgAppRemovalItem.organisation?.id },
            communityApp: { id: orgApp?.id },
          },
          orgAppRemovalItem.subscriptionId,
          clearItem,
          resource,
          t
        );
      }
    }
  }, [
    orgAppRemovalItem,
    orgAppSubscriptionsLookupFetch.value,
    dispatchRemoveOrgAppSubscriptionDelete,
    dispatchUpdateOrgAppSubscriptionPut,
    feature,
    resource,
    t,
  ]);

  if (!appId || !installationId || !featureId) {
    return <span>{t('noSubscriptionsFound')}</span>;
  }

  return (
    <Flex fluid direction="column">
      <Flex>
        {!feature?.isGlobal && (
          <HasRight right="apps/installations/features/subscriptions/create">
            <Button primary icon="plus" content={t('addSubscription')} onClick={setAddModalOpen} />
          </HasRight>
        )}
        {canCreate && (
          <HasRight right="apps/installations/features/subscriptions/create">
            <Button primary icon="plus" content={t('createSubscription')} onClick={setModalOpen} />
          </HasRight>
        )}
      </Flex>
      <Table
        key={refreshKey}
        resource={resource}
        url={apiRoutes.featureSubscriptions(appId, installationId, featureId)}
        noResultsMessage={t('noSubscriptionsFound')}
        pageSize={50}
        filtered
        sorted
        resizable
      >
        <Params.String
          name="assignmentType"
          label={t('assignmentType')}
          width="10rem"
          noSort
          noFilter
        />
        <Params.String
          name="organisation.name"
          label={t('organisation')}
          sortName="organisationName"
          filterName="organisationName"
          link={`/organisations/:organisation.id/${TABS.DETAILS}`}
          width="20rem"
          grow
        />
        <Params.String
          name="community.name"
          label={t('community')}
          sortName="communityName"
          filterName="communityName"
          link={`/communities/${ACTIONS.EDIT}/:community.id/${COMMUNITY_DETAIL_TABS.DETAILS}`}
          width="18rem"
          grow
        />
        <Params.DateTime
          name="createdOn"
          label={t('createdOn')}
          width="10rem"
          sortName="createdOn"
          noFilter
        />
        <Params.Custom
          name="subscriptionId"
          content={PopupAndCopy}
          position="top right"
          label={t('subscriptionId')}
          width="20rem"
          grow
          noSort
          noFilter
        />
        {feature?.isGlobal && feature?.requiresSubscription && (
          <Merge
            width="4.5rem"
            align="center"
            type="Menu"
            hasRight="apps/installations/features/subscriptions/delete"
          >
            <Menu width="3.5rem" align="center">
              <Actions.Button
                icon="trash"
                label={t('removeSubscription')}
                content={t('removeSubscription')}
                onClick={handleSetRemovalItem}
              />
            </Menu>
          </Merge>
        )}
        {!feature?.isGlobal && (
          <Merge
            width="4.5rem"
            align="center"
            type="Menu"
            hasRight="apps/installations/features/subscriptions/delete"
            as={SubscriptionMenu}
          >
            <Menu width="3.5rem" align="center">
              <Actions.Button
                icon="trash"
                label={t('removeOrgSubscription')}
                content={t('removeOrgSubscription')}
                onClick={handleSetOrgAppRemovalItem}
              />
            </Menu>
          </Merge>
        )}
      </Table>
      <CreateFeatureSubscriptionModal
        open={modalOpen}
        appId={appId}
        installationId={installationId}
        featureId={featureId}
        onClose={setModalClosed}
        resource={resource}
      />
      {addModalOpen && (
        <AddSubscriptionModal
          appId={appId}
          installationId={installationId}
          featureId={featureId}
          featureTag={feature?.tag}
          onClose={setAddModalClosed}
          onCreated={handleSubscriptionCreated}
        />
      )}
      {removalItem && (
        <TypeToConfirmModal
          onConfirm={handleRemove}
          onCancel={handleCancel}
          header={t('areYouSureRemoveSubscription', {
            organisationName: removalItem?.organisation?.name,
          })}
          loading={removeFeatureSubscriptionFetch.pending}
          value={removalItem?.organisation?.name}
        />
      )}
      <ConfirmModal
        open={!!orgAppRemovalItem}
        header={t('areYouSureRemoveOrgSubscription', {
          featureName: feature?.name,
          installationName,
          appName,
          organisationName: orgAppRemovalItem?.organisation?.name,
        })}
        onConfirm={handleOrgAppRemove}
        onCancel={handleCancelOrgAppRemove}
        cancelContent={t('cancel')}
        confirmContent={t('removeOrgSubscription')}
        confirmLoading={
          removeOrgAppSubscriptionFetch.pending ||
          updateOrgAppSubscriptionFetch.pending ||
          orgAppSubscriptionsLookupFetch.pending
        }
      />
    </Flex>
  );
};

FeatureSubscriptions.propTypes = {
  appId: PropTypes.string.isRequired,
  installationId: PropTypes.string.isRequired,
  featureId: PropTypes.string.isRequired,
  installationName: PropTypes.string,
  appName: PropTypes.string,
  canCreate: PropTypes.bool,
  dispatchRemoveFeatureSubscriptionDelete: PropTypes.func.isRequired,
  removeFeatureSubscriptionFetch: PropTypes.object.isRequired,
  dispatchRemoveOrgAppSubscriptionDelete: PropTypes.func.isRequired,
  removeOrgAppSubscriptionFetch: PropTypes.object.isRequired,
  dispatchUpdateOrgAppSubscriptionPut: PropTypes.func.isRequired,
  updateOrgAppSubscriptionFetch: PropTypes.object.isRequired,
  dispatchOrgAppSubscriptionsLookupGet: PropTypes.func.isRequired,
  orgAppSubscriptionsLookupFetch: PropTypes.object.isRequired,
  feature: PropTypes.object,
};

FeatureSubscriptions.defaultProps = {
  canCreate: false,
  feature: undefined,
  installationName: undefined,
  appName: undefined,
};

export default compose(
  removeFeatureSubscriptionService,
  removeOrgAppSubscriptionService,
  updateOrgAppSubscriptionService,
  orgAppSubscriptionsLookupService
)(FeatureSubscriptions);
